import React, { useEffect, useRef, useState } from 'react';
import './ManageUpdateAccess.css';
import DefaultHeading from '../../components/heading/DefaultHeading';
import QuestionHeading from '../../components/heading/QuestionHeading';
import BorderHeading from '../../components/heading/BorderHeading';
import { fetchUpdateAccessConfig } from '../../services/manageUpdateAccessService';
import { manageUpdateAccessObj } from './manageUpdateAccessTypes';
import { openPopup } from '../../stateManagement/actions/popupActions';
import { CircularProgress, TextField } from '@mui/material';
import { updateClientConfig, updateConfigRequestBody } from '../../services/managePoliciesService';
import CountrySelect from '../../components/countrySelect/CountrySelect';
import { CountryType } from '../../components/countrySelect/CountryCodes';
import { validateWithOtp } from '../../stateManagement/actions/validateOtpPopupActions';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { Loader } from '../../components/loader/Loader';
import { getNewConfiguration, getUpdateNewConfiguration, postConfiguration } from '../../services/uiConfigService';
import { defaultUpdateAccessConfig } from './manageUpdateAccessConfig';
import { TranslationEnum, usePortalTranslation } from '../../providers/TranslationProvider';
import { metaDataBatchPayload } from '../../types/types';
import { META_DATA_BATCH_API } from '../../services/networkService';

export function MarginContainer({margin, padding, children }: {margin?: string, padding?: string, children: React.ReactNode}){
  return <div style={{margin,padding}}>
    {children}
  </div>
}

function ManageUpdateAccess() {
  const { translate } = usePortalTranslation();
  const CUR_COMPONENT = "Manage Update Access"
  const CUR_COMPONENT_COMMON="commonPortal";
  const [adminUsers,setAdminUsers] = useState<manageUpdateAccessObj[]>([]);
  const [normalUsers,setNormalUsers] = useState<manageUpdateAccessObj[]>([]);
  const [showSubmitLoader,setShowSubmitLoader] = useState<boolean>(false);
  const [phoneNumber,setPhoneNumber] = useState<string>("");
  const [userName,setUserName] = useState<string>("");
  const updateAccessConfig = useRef<any>(null);
  const [countryCode,setCountryCode] = useState<CountryType>({ code: 'IN', label: 'India', phone: '91' });
  const navigate = useNavigate();
  const [showLoader,setShowLoader] = useState<boolean>(false);
  const didConfigExists = useRef<boolean>(true);

  async function getAndSetAllowedUsers(){
    try{
      setShowLoader(true);
      let manageUpdateAccessConfig = (await getNewConfiguration('update_access_configuration'))?.domainValues;
      if(!manageUpdateAccessConfig){
        didConfigExists.current = false;
        manageUpdateAccessConfig = defaultUpdateAccessConfig;
      }
      const tempAdminUsers = manageUpdateAccessConfig[0].value.filter((user: manageUpdateAccessObj) => user.isAdmin);
      const tempNormalUsers = manageUpdateAccessConfig[0].value.filter((user: manageUpdateAccessObj) => !user.isAdmin);
      
      updateAccessConfig.current = manageUpdateAccessConfig;
      setAdminUsers(tempAdminUsers);
      setNormalUsers(tempNormalUsers);
      setShowLoader(false);
    }catch(err){
      console.log(err);
      openPopup("Error","The config does not exist, please add config");
    }
  }
  useEffect(() => {
    getAndSetAllowedUsers();
  },[])

  function validateData(currentNumber: string, name: string){
    if(!currentNumber.trim()){
      return "Please enter a valid phone number";
    }
    const isAlreadyPresent = updateAccessConfig.current[0].value.find((user: manageUpdateAccessObj) => user.phoneNumber === currentNumber);
    if(isAlreadyPresent){
      return "Entered Phone number already exist, Please enter different number";
    }
    if(!name.trim()){
      return "Name can't be empty";
    }
    if(name.length > 20){
      return "Name can be of maximum 20 characters";
    }
    return "";
  }
  async function handleNumberDelete(targetNumber: string){
    try{
        const newUpdateAccessConfig = [...updateAccessConfig.current];
        const filteredNumbers = updateAccessConfig.current[0].value.filter((user: manageUpdateAccessObj) => {
          return user.phoneNumber !== targetNumber;
        });
        newUpdateAccessConfig[0].value = filteredNumbers;

        let updateAccessPayload: updateConfigRequestBody = {
          domainName: "clientconfig",
          domainType: "update_access_configuration",
          domainValues: newUpdateAccessConfig ,
          lob: JSON.parse(localStorage.authContext).user.lob,
        };
        const finalMetaDataPayload: metaDataBatchPayload = { features: [updateAccessPayload] };
        validateWithOtp(async (verifiedUser?: manageUpdateAccessObj,verifyResponse?: any) => {
          if(verifyResponse.status===200 || verifyResponse.status===201){
            openPopup("Success",`Phone number: ${targetNumber} now does not have OTP access`,() => {
              navigate(0);
            });
          }else{
            openPopup("Error","Something went wrong");
          }
      },["uat","demo","prod","prod-egtm"],"admin",META_DATA_BATCH_API,finalMetaDataPayload,"PUT")
    }catch(err){
      console.log(err);
      openPopup("Error","Something went wrong");
    }
  }
  async function handleSubmit(){
    try{
      setShowSubmitLoader(true);
      const validationMessage = validateData(phoneNumber,userName);
      if(validationMessage!==""){
        openPopup("Alert",validationMessage);
        setShowSubmitLoader(false);
        return;
      }
      // validateWithOtp(doFinalSubmit,["uat","demo","prod","prod-egtm"],"admin");
      await doFinalSubmit()
    }catch(err){
      console.log(err);
      openPopup("Error","Something went wrong");
    }finally{
      setShowSubmitLoader(false);
    }
  }
  async function doFinalSubmit(verifiedUser?: manageUpdateAccessObj){
    const newUpdateAccessConfig = [...updateAccessConfig.current];
      newUpdateAccessConfig[0].value.push({
        phoneNumber,
        countryCode: countryCode.phone,
        isAdmin: false,
        userName
      })
      let updateAccessPayload = {
        domainName: "clientconfig",
        domainType: "update_access_configuration",
        domainValues: newUpdateAccessConfig ,
        lob: JSON.parse(localStorage.authContext).user.lob,
      };
      const finalMetaDataPayload: metaDataBatchPayload = { features: [updateAccessPayload] };
      validateWithOtp(async (verifiedUser?: manageUpdateAccessObj,verifyResponse?: any) => {
        if(verifyResponse.status===200 || verifyResponse.status===201){
          openPopup("Success",`Phone number: ${phoneNumber} now has OTP access`,() => {
            navigate(0);
          });
        }else{
          openPopup("Error","Something went wrong");
        }
    },["uat","demo","prod","prod-egtm"],"admin",META_DATA_BATCH_API,finalMetaDataPayload,"PUT");
  }
  return (
    !showLoader?(<div className='manage-update-access-parent'>
      <DefaultHeading heading={translate(TranslationEnum.manage_update_access,"Manage Update Access")} showDefaultBackButton={true}></DefaultHeading>
      <div className='manage-update-access-boxes-container'>
        <div className='manage-update-access-box manage-update-access-current-users-box'>
          <BorderHeading>{translate(TranslationEnum.manage_update_access,"Current Users")}</BorderHeading>
          <MarginContainer margin="20px">
            <QuestionHeading>{translate(TranslationEnum.manage_update_access,"Admins")}</QuestionHeading>
            <MarginContainer margin="20px">
              <ul className='manage-update-access-users-list'>
                {adminUsers.map((userObj: manageUpdateAccessObj) => {
                  return (
                    <li key={userObj.phoneNumber} className='manage-update-access-user'>
                      <div className='manage-update-access-user-phone-container'>
                        <span className='manage-update-access-user-country-code'>+{userObj.countryCode}</span>
                        <span>{userObj.phoneNumber}</span>
                        {userObj.userName && <span>{` - ${userObj.userName}`}</span>}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </MarginContainer>
            <QuestionHeading>{translate(TranslationEnum.manage_update_access,"Other Users")}</QuestionHeading>
            <MarginContainer margin='20px'>
              <ul className='manage-update-access-users-list'>
                {normalUsers.map((userObj: manageUpdateAccessObj) => {
                  return (
                    <li key={userObj.phoneNumber} className='manage-update-access-user'>
                      <div className='manage-update-access-user-phone-container'>
                        <span className='manage-update-access-user-country-code'>+{userObj.countryCode}</span>
                        <span>{userObj.phoneNumber}</span>
                        {userObj.userName && <span>{` - ${userObj.userName}`}</span>}
                      </div>
                      <div className='manage-update-access-user-trash-container'>
                        <FontAwesomeIcon className='manage-update-access-user-trash' onClick={() => handleNumberDelete(userObj.phoneNumber)} icon={faTrash} />
                      </div>
                    </li>
                  )
                })}
              </ul>
            </MarginContainer>
          </MarginContainer>
        </div>
        <div className='manage-update-access-box manage-update-access-add-user-box'>
          <BorderHeading>{translate(TranslationEnum.manage_update_access,"ADD NEW USER")}</BorderHeading>
          <MarginContainer margin="20px">
            <>
            <TextField
                id="outlined-start-adornment"
                className="manage-update-access-input-fields"
                size="small"
                value={userName}
                label={translate(TranslationEnum.manage_update_access,"User Name")}
                onChange={(event) => {
                  setUserName(event.target.value)
                }}
              />
              <CountrySelect className="manage-update-access-input-fields" countryCode={countryCode} setCountryCode={setCountryCode} params={{ size: "small" }}/>
              <TextField
                id="outlined-start-adornment"
                className="manage-update-access-input-fields"
                size="small"
                type="number"
                value={phoneNumber}
                label={translate(TranslationEnum.manage_update_access,"Phone Number")}
                onChange={(event) => {
                  setPhoneNumber(event.target.value)
                }}
              />
              <div className="create-bucket-submit-button-container">
                <button
                  onClick={() => handleSubmit()}
                  className="create-bucket-submit-button"
                  disabled={showSubmitLoader}
                >
                  {showSubmitLoader? (
                    <div>
                      <CircularProgress size={15} color="inherit" />
                      <span className="circular-progress-container ">{translate(TranslationEnum.common_portal,"SUBMIT")}</span>
                    </div>
                  ) : (
                    <>{translate(TranslationEnum.common_portal,"SUBMIT")}</>
                  )}
                </button>
              </div>
            </>
          </MarginContainer>
        </div>
      </div>
    </div>):(<Loader />)
  )
}

export default ManageUpdateAccess;